import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider } from "@/contexts/AuthContext";
import CookieConsent from "@/components/CookieConsent";
import PageTranslator from "@/components/PageTranslator";
import TranslationOverlay from "@/components/TranslationOverlay";
import ScrollToTop from "@/components/ScrollToTop";
import { useCmsRealtime } from "@/hooks/useCmsRealtime";
import { useAnalytics } from "@/hooks/useAnalytics";

// Eager load: landing page (LCP critical)
import Index from "./pages/Index";

// Lazy load: all other routes
const HausPanorama = lazy(() => import("./pages/HausPanorama"));
const HausStrandnixe = lazy(() => import("./pages/HausStrandnixe"));
const ApartmentDetail = lazy(() => import("./pages/ApartmentDetail"));
const StrandnixeDetail = lazy(() => import("./pages/StrandnixeDetail"));
const Umgebung = lazy(() => import("./pages/Umgebung"));
const Bildergalerie = lazy(() => import("./pages/Bildergalerie"));
const Kontakt = lazy(() => import("./pages/Kontakt"));
const Impressum = lazy(() => import("./pages/Impressum"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const AGB = lazy(() => import("./pages/AGB"));
const Login = lazy(() => import("./pages/Login"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
    },
  },
});

const PageLoader = () => (
  <div className="min-h-dvh flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

/**
 * Realtime-Subscriptions laufen nur im Admin-Bereich.
 * Auf öffentlichen Seiten wird keine WebSocket-Verbindung geöffnet.
 */
const RealtimeBridge = () => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  return isAdmin ? <AdminRealtime /> : null;
};

const AdminRealtime = () => {
  useCmsRealtime();
  return null;
};

const AnalyticsBridge = () => {
  useAnalytics();
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <div>
      <AuthProvider>
        <LanguageProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <RealtimeBridge />
              <ScrollToTop />
              <PageTranslator />
              <TranslationOverlay />
              <AnalyticsBridge />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/ferienwohnungen/haus-panorama-duhnen" element={<HausPanorama />} />
                  <Route path="/ferienwohnungen/haus-panorama-duhnen/:id" element={<ApartmentDetail />} />
                  <Route path="/ferienwohnungen/haus-strandnixe-doese" element={<HausStrandnixe />} />
                  <Route path="/ferienwohnungen/haus-strandnixe-doese/:id" element={<StrandnixeDetail />} />
                  <Route path="/wissenswertes/die-umgebung" element={<Umgebung />} />
                  <Route path="/wissenswertes/bildergalerie" element={<Bildergalerie />} />
                  <Route path="/kontakt" element={<Kontakt />} />
                  <Route path="/impressum" element={<Impressum />} />
                  <Route path="/datenschutzerklaerung" element={<Datenschutz />} />
                  <Route path="/agb" element={<AGB />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/unsubscribe" element={<Unsubscribe />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
            <CookieConsent />
          </TooltipProvider>
        </LanguageProvider>
      </AuthProvider>
    </div>
  </QueryClientProvider>
);

export default App;
